import { useCallback, useMemo } from 'react';

import AutocompleteWithChips from 'components/AutocompleteWithChips/AutocompleteWithChips';
import { LookupDateRangeQueryItemDto } from 'types/CodegenLookupTypes';
import { SelectedItemDto } from 'types/CodegenReportTypes';
import { usePostLookupUmpireSessions } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';
import { displayDate } from 'utilities/dateHelpers';

type Props = {
  selectedSession: SelectedItemDto | undefined;
  onChange: (session: SelectedItemDto) => void;
  dateRanges: LookupDateRangeQueryItemDto[] | undefined;
  leagueIds: string[] | undefined;
  umpireIds: string[] | undefined;
};

const SelectUmpireSession = ({ selectedSession, onChange, dateRanges, leagueIds, umpireIds }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = usePostLookupUmpireSessions(dateRanges, leagueIds, umpireIds);

  const allSessionIds = useMemo(() => data?.map((datum) => datum.id) ?? [], [data]);

  const getSessionName = useCallback(
    (sessionId: string) => {
      const sessionNames = new Map(
        data?.map((datum) => [datum.id, `${displayDate(datum.gameDate)}${datum.game.length > 4 ? ` | ${datum.game}` : ''}`])
      );
      sessionNames.set('', ''); // needed by autocomplete to reset properly
      return sessionNames.get(sessionId) ?? (t('Nonexistent session') as string);
    },
    [data]
  );

  return (
    <AutocompleteWithChips
      label={t('Sessions')}
      options={allSessionIds}
      selected={selectedSession?.id ?? ''}
      setSelected={(selectedId) => onChange({ id: selectedId, name: getSessionName(selectedId) })}
      getOptionLabel={(option) => (isLoading ? t('Loading sessions...') : getSessionName(option))}
      noOptionsText={isLoading ? t('Loading sessions...') : t('No sessions found')}
      disabled={!dateRanges || !leagueIds || !umpireIds}
      single
    />
  );
};

export default SelectUmpireSession;
